import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { EventTypes, PathService } from '@site-mate/dashpivot-shared-library';

import { BannerKeys } from 'app/shared/model/banner-keys.enum';
import { BannerMetadata } from 'app/shared/model/banner-metadata.model';
import { AccountService } from 'app/shared/service/account.service';
import { CompanyService } from 'app/shared/service/company.service';
import { EventsService } from 'app/shared/service/events/events.service';
import { UserService } from 'app/user/user.service';
import { environment } from 'environments/environment';

import { FlowsiteBannerKeyByTemplateReference } from './flowsite-banner-key-by-template-reference.model';
import { FlowsiteBannerMetadataList } from './flowsite-banner-metadata.model';
import { FlowsiteBannerPopoverMetadata } from './flowsite-banner-popover-metadata.model';
import { FlowsiteReferralEventContext } from './flowsite-referral-event-context.model';
import { FlowsiteTemplateReference } from './flowsite-template-reference.model';

@Component({
  selector: 'cc-flowsite-banner',
  templateUrl: './flowsite-banner.component.html',
  styleUrls: ['./flowsite-banner.component.scss'],
})
export class FlowsiteBannerComponent implements OnInit {
  private readonly baseLink = `${environment.flowsite.uiUrl}/signup?reference=`;

  private accountId: string;

  protected readonly isEnabled = environment.featureToggles.flowsiteReferralBanner;

  readonly flowsiteReferenceTypes = FlowsiteTemplateReference;
  readonly flowsiteBannerPopoverMetadata = FlowsiteBannerPopoverMetadata;

  readonly textByBannerType = {
    [FlowsiteTemplateReference.DashpivotXeroTimesheets]: 'Timesheets',
    [FlowsiteTemplateReference.DashpivotXeroDockets]: 'Dockets',
  };

  bannerKey: BannerKeys;
  localStorageKey: string;
  flowsiteReferenceType: FlowsiteTemplateReference;
  referenceLink: string;
  isNotAPowerBiOrExcelBanner: boolean;
  shouldShowBanner: boolean = false;

  @Input({ required: true }) templateName!: string;
  @Input() templatePath: string;
  @Input() companyId: string;

  @Input() priority: number;
  @Input() parentComponent: string;

  constructor(
    private readonly eventsService: EventsService,
    private readonly pathService: PathService,
    private readonly companyService: CompanyService,
    private readonly userService: UserService,
    private readonly accountService: AccountService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    if (!this.companyId) {
      const [companyId] = this.pathService.extractFolderIds(this.templatePath);
      this.companyId = companyId;
    }

    await this.setAccountId();

    const isContributor = this.isUserAContributor();
    const flowsiteWorkspaces = await this.getFlowsiteWorkspaces();

    this.shouldShowBanner = this.isEnabled && isContributor && !(flowsiteWorkspaces?.length > 0);
    this.flowsiteReferenceType = this.getBannerType();
    if (this.flowsiteReferenceType) {
      this.bannerKey = FlowsiteBannerKeyByTemplateReference[this.flowsiteReferenceType];
      this.localStorageKey = BannerMetadata[this.bannerKey].localStorageKey;
      this.isNotAPowerBiOrExcelBanner = Object.keys(this.textByBannerType).includes(
        this.flowsiteReferenceType,
      );

      this.setReferenceLink();

      this.changeDetectorRef.detectChanges();
    }
  }

  trackFlowsiteReferral() {
    this.eventsService.trackEvent(EventTypes.FlowsiteReferralClicked, {
      Context: FlowsiteReferralEventContext[this.flowsiteReferenceType],
    });
  }

  private getBannerType() {
    const templateName = this.templateName.toLowerCase();
    const bannerType = FlowsiteBannerMetadataList.find((metadata) => metadata.regex.test(templateName));

    return bannerType?.reference || null;
  }

  private isUserAContributor() {
    const user = this.userService.getCurrentUser();
    const isUserLoggedIn = Boolean(user._id); // Handles embed template on IFrame pages
    const isUserAVisitorOfCompany = user.visitorOf?.includes(this.companyId);

    return user?.isGlobalAdmin || (isUserLoggedIn && !isUserAVisitorOfCompany);
  }

  private async setAccountId() {
    const company = await lastValueFrom(this.companyService.getCompany(this.companyId));

    this.accountId = company?.accountId;
  }

  private async getFlowsiteWorkspaces() {
    return lastValueFrom(this.accountService.getFlowsiteWorkspaces(this.accountId));
  }

  private setReferenceLink() {
    const { baseLink, flowsiteReferenceType, companyId, accountId } = this;

    this.referenceLink = `${baseLink}${flowsiteReferenceType}&sitemateStartAccountId=${accountId}&workspaceId=${companyId}`;
  }
}
